import { useState, useEffect } from "react";
import { logger } from "@hopper-b2b/datadog";
import { useDebounce } from "./useDebounce";

// Diagnostics for the iOS Safari viewport issues.
const DEBUG_VIEWPORT = import.meta.env.MODE !== "production";

// Context is built lazily: gathering it reads visualViewport/activeElement,
// which forces layout, and resize can fire dozens of times per second.
const log = (message: string, getContext?: () => Record<string, unknown>) => {
  if (DEBUG_VIEWPORT) {
    logger()?.info(message, getContext?.());
  }
};

/**
 * Check if an input-like element is currently focused (keyboard likely visible)
 */
const isInputFocused = (): boolean => {
  const activeElement = document.activeElement;
  if (!activeElement) return false;

  const tagName = activeElement.tagName.toLowerCase();
  const isInput = tagName === "input" || tagName === "textarea";
  const isContentEditable =
    activeElement.getAttribute("contenteditable") === "true";

  return isInput || isContentEditable;
};

const useWindowSize = (delayInMs?: number) => {
  const [windowHeight, setWindowHeight] = useState(
    document.documentElement.clientHeight
  );
  const [windowWidth, setWindowWidth] = useState(
    document.documentElement.clientWidth
  );

  const debouncedWindowHeight = useDebounce(windowHeight, delayInMs ?? 1000);
  const debouncedWindowWidth = useDebounce(windowWidth, delayInMs ?? 0);

  useEffect(() => {
    const handleWindowResize = () => {
      const newWidth = document.documentElement.clientWidth;
      const newHeight = document.documentElement.clientHeight;
      const inputFocused = isInputFocused();

      log("[useWindowSize] resize event", () => ({
        newHeight,
        newWidth,
        prevHeight: windowHeight,
        prevWidth: windowWidth,
        inputFocused,
        activeElement: document.activeElement?.tagName,
        activeElementId: (document.activeElement as HTMLElement)?.id,
        visualViewport: window.visualViewport
          ? {
              height: window.visualViewport.height,
              width: window.visualViewport.width,
              offsetTop: window.visualViewport.offsetTop,
              scale: window.visualViewport.scale,
            }
          : "not supported",
        scrollY: window.scrollY,
        inIframe: window.self !== window.top,
        userAgent: navigator.userAgent.includes("iPhone") ? "iPhone" : "other",
      }));

      // Always update width
      setWindowWidth(newWidth);

      // Only update height if:
      // 1. No input is focused (keyboard not visible), OR
      // 2. The height INCREASED (keyboard dismissed), OR
      // 3. The width changed (orientation change)
      // This prevents jumpy layout when keyboard appears/autocomplete shows in iOS Safari iframe
      const shouldUpdateHeight =
        !inputFocused || newHeight > windowHeight || newWidth !== windowWidth;

      log("[useWindowSize] shouldUpdateHeight", () => ({
        shouldUpdateHeight,
        inputFocused,
        heightIncreased: newHeight > windowHeight,
        widthChanged: newWidth !== windowWidth,
      }));

      if (shouldUpdateHeight) {
        setWindowHeight(newHeight);
      } else {
        log(
          "[useWindowSize] SKIPPING height update (input focused, height decreased, width same)"
        );
      }
    };

    // Hack to handle scrollbar appearing at the time of page load since scrollbar changes the window size
    // but does not trigger a resize event.
    setTimeout(() => handleWindowResize(), 500);

    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
  }, [windowHeight, windowWidth]);

  return { height: debouncedWindowHeight, width: debouncedWindowWidth };
};

export { useWindowSize };
